import React, { useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import StarBorderRoundedIcon from '@mui/icons-material/StarBorderRounded';
import { Button, Card, Chip, Grid } from '@mui/material';
import Typography from '@mui/material/Typography';
import { doc, getDoc, onSnapshot } from 'firebase/firestore';
import { useTranslation } from 'react-i18next';

import { getMediaImage } from '../../api/aws/s3';
import { mediasRef } from '../../api/firebase/firestore/media';
import noImage from '../../components/assets/noImage.svg';
import { black, blueGrayText } from '../../constants/Colors';
import { cardContentPadding, cardSize } from '../../constants/Location';
import {
  categoryList,
  mediaTypeList,
  targetList,
  targetTypeList
} from '../../constants/Select';
import { AuthContext } from '../../contexts/auth';
import { throwError } from '../../modules/error';

import type { MediaType } from '../../api/firebase/firestore/media/mediaType';
import type { ArticleType } from '../../api/microcms/article/articleType';
import type { SelectOption } from '../../constants/Select';

const styles = {
  articleTitle: {
    color: black,
    fontSize: '18px',
    fontWeight: 700,
    mb: '16px'
  },
  card: {
    boxShadow: 'none',
    margin: { sm: '0 24px', xs: '0 16px' }
  },
  cardContent: {
    padding: { sm: '32px', xs: '16px' },
    rowGap: '16px'
  },
  cardContentPadding,
  cardSize,
  date: {
    color: blueGrayText,
    fontSize: '14px'
  },
  editButton: {
    color: '#222'
  },
  favoriteCount: {
    color: 'rgba(0, 0, 0, 0.54)'
  },
  image: {
    height: { lg: '95%', xs: '100%' },
    mb: '16px',
    position: 'relative',
    width: { lg: '95%', xs: '100%' }
  },
  itemText: {
    color: black,
    fontSize: '14px'
  },
  itemTitle: {
    color: blueGrayText,
    fontSize: '14px'
  },
  label: {
    '.MuiChip-label': {
      padding: '0 12px'
    },
    backgroundColor: 'rgba(10, 126, 191, 0.08)',
    border: '1px solid ##0A7EBF',
    borderRadius: '4px',
    fontSize: '12px',
    height: '24px'
  },
  mediaName: {
    color: black,
    fontSize: '14px'
  },
  status: {
    '.MuiChip-label': {
      padding: '0'
    },
    borderRadius: '2px',
    color: '#FFF',
    fontSize: '12px',
    height: '24px',
    left: '11px',
    padding: '0 10px',
    position: 'absolute',
    top: '8px'
  }
};

interface Props {
  article: ArticleType;
}

interface ArticleCardType {
  articleId: string;
  category: string[];
  created: string;
  draftFlg: boolean;
  image: string;
  mediaName: string;
  mediaType: string;
  mediaId: string;
  target: string[];
  targetType: string;
  title: string;
  updated: string;
}

const ArticleCard = (props: Props): JSX.Element => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { currentUser } = useContext(AuthContext);
  const { article } = props;
  const cateList = categoryList();
  const mTypeList = mediaTypeList();
  const tTypeList = targetTypeList();
  const taList = targetList();
  const mediaId = article.mediaId;
  const [data, setData] = useState<ArticleCardType>({
    articleId: '',
    category: [],
    created: '',
    draftFlg: false,
    image: '',
    mediaId: '',
    mediaName: '',
    mediaType: '',
    target: [],
    targetType: '',
    title: '',
    updated: ''
  });
  const [favoriteCount, setFavoriteCount] = useState<number>(0);

  const showValueToName = (
    selectOption: SelectOption[],
    itemValue: number | undefined
  ): string => {
    const d = selectOption.find((element) => element.value === itemValue);
    return d?.option ?? '';
  };

  const showMediaType = (v: number): string => {
    const datas = mTypeList.map((item) =>
      item.options.find((i) => i.value === v)
    );
    const md = datas.filter((item) => item !== undefined);
    return (md[0]?.option as string) ?? '';
  };

  const formattedDate = (date: string | undefined): string | undefined => {
    if (date) {
      const d = new Date(date);
      const year = d.getFullYear();
      const month = d.getMonth() + 1;
      const day = d.getDate();
      return `${year}/${month}/${day}`;
    }
  };

  useEffect(() => {
    void (async () => {
      if (currentUser) {
        const mediaRef = doc(mediasRef, article.mediaId);
        const mediaSnap = await getDoc(mediaRef);
        const info = mediaSnap.data() as MediaType;

        const categoryNames: string[] = [];
        info?.category.map((i: number) => {
          const name = showValueToName(cateList, i);
          return categoryNames.push(name);
        });

        const mediaType = showMediaType(info?.mediaType);

        const targetNames: string[] = [];
        if (info.target) {
          info.target.map((i: number) => {
            const name = showValueToName(taList, i);
            return targetNames.push(name);
          });
        }

        const targetType = showValueToName(tTypeList, info?.targetType);

        let image;
        if (article.imagePath) {
          image = await getMediaImage(article.imagePath as unknown as string);
        }

        setFavoriteCount(info.favoriteCount ? info.favoriteCount : 0);

        setData({
          articleId: info?.articleId,
          category: categoryNames,
          created: article.createdAt ?? '',
          draftFlg: info?.draftFlg,
          image: image ?? noImage,
          mediaId: info?.mediaId,
          mediaName: info?.name,
          mediaType,
          target: targetNames ?? '',
          targetType,
          title: article.title ?? '',
          updated: article.updatedAt ?? ''
        });
      }
    })();
  }, [article]);

  useEffect(() => {
    try {
      if (currentUser) {
        const mediaRef = doc(mediasRef, mediaId);
        // お気に入り数のリアルタイム更新処理
        const unsubscribe = onSnapshot(mediaRef, (snap) => {
          const realtimeCount = snap.data()?.favoriteCount as number;
          setFavoriteCount(realtimeCount);
        });

        return () => unsubscribe();
      }
    } catch (e) {
      throwError(e);
    }
  }, [mediaId]);

  return (
    <>
      <Card sx={styles.card}>
        <Grid container sx={styles.cardContent}>
          <Grid item xs={12} lg={6} sx={{ minHeight: '117px' }}>
            <Grid sx={styles.image}>
              <img
                src={data.image ?? noImage}
                style={{
                  height: 'auto',
                  objectFit: 'contain',
                  width: '100%'
                }}
                alt="mediaImage"
              />
              <Chip
                label={data.draftFlg ? '下書き' : '配信中'}
                sx={[
                  styles.status,
                  {
                    bgcolor: data.draftFlg
                      ? 'rgba(34, 34, 34, 0.72)'
                      : 'rgba(189, 0, 23, 0.72)'
                  }
                ]}
              />
            </Grid>
          </Grid>
          <Grid
            item
            xs={12}
            lg={6}
            container
            sx={{ alignContent: 'flex-start', rowGap: '16px' }}
          >
            <Grid
              container
              flexDirection="row"
              justifyContent="space-between"
              alignItems="center"
            >
              <Grid>
                <Grid
                  container
                  flexDirection="row"
                  alignItems="center"
                  sx={{
                    alignItems: { lg: 'center', xs: 'flex-favoriteCountt' },
                    columnGap: '16px',
                    flexDirection: { lg: 'row', xs: 'column' },
                    rowGap: { lg: '0px', xs: '8px' }
                  }}
                >
                  <Grid item>
                    <Grid container flexDirection="row" columnGap="8px">
                      {data.category?.map((item, index) => (
                        <Chip
                          sx={styles.label}
                          label={item}
                          color="primary"
                          variant="outlined"
                          key={index}
                        />
                      ))}
                    </Grid>
                  </Grid>
                  <Grid item>
                    <Grid
                      container
                      flexDirection="row"
                      sx={{ columnGap: '16px' }}
                    >
                      <Typography component="p" sx={styles.date}>
                        {article.status === '下書き'
                          ? `${t('mypage.articleList.draft')}：${
                              formattedDate(data.created) ?? ''
                            }`
                          : `${t('mypage.articleList.createdDate')}：${
                              formattedDate(data.created) ?? ''
                            }`}
                      </Typography>
                      <Typography component="p" sx={styles.date}>
                        {t('mypage.articleList.updatedDate')}
                        {formattedDate(data.updated)}
                      </Typography>
                    </Grid>
                  </Grid>
                </Grid>
              </Grid>
              <Grid>
                <Grid container flexDirection="row" sx={{ columnGap: '4px' }}>
                  <StarBorderRoundedIcon sx={styles.favoriteCount} />
                  <Typography component="p" sx={styles.favoriteCount}>
                    {favoriteCount ?? 0}
                  </Typography>
                </Grid>
              </Grid>
            </Grid>
            <Grid>
              <Typography component="p" sx={styles.articleTitle}>
                {data.title}
              </Typography>
              <Typography sx={styles.mediaName} component="p">
                {data.mediaName}
              </Typography>
            </Grid>
            <Grid container alignItems="end" sx={{ rowGap: '16px' }}>
              <Grid container item xs={12} lg={8} sx={{ rowGap: '8px' }}>
                <Grid container flexDirection="row">
                  <Grid item xs={4}>
                    <Typography component="p" sx={styles.itemTitle}>
                      {t('mypage.articleList.category')}
                    </Typography>
                  </Grid>
                  <Grid item xs={8}>
                    <Typography component="p" sx={styles.itemText}>
                      {data.category?.join('、')}
                    </Typography>
                  </Grid>
                </Grid>
                <Grid container flexDirection="row">
                  <Grid item xs={4}>
                    <Typography component="p" sx={styles.itemTitle}>
                      {t('mypage.articleList.mediaType')}
                    </Typography>
                  </Grid>
                  <Grid item xs={8}>
                    <Typography component="p" sx={styles.itemText}>
                      {data.mediaType}
                    </Typography>
                  </Grid>
                </Grid>
                <Grid container flexDirection="row">
                  <Grid item xs={4}>
                    <Typography component="p" sx={styles.itemTitle}>
                      {t('mypage.articleList.target')}
                    </Typography>
                  </Grid>
                  <Grid item xs={8}>
                    <Typography component="p" sx={styles.itemText}>
                      {data.target.join('、')}
                    </Typography>
                  </Grid>
                </Grid>
                <Grid container flexDirection="row">
                  <Grid item xs={4}>
                    <Typography component="p" sx={styles.itemTitle}>
                      {t('mypage.articleList.targetType')}
                    </Typography>
                  </Grid>
                  <Grid item xs={8}>
                    <Typography component="p" sx={styles.itemText}>
                      {data.targetType}
                    </Typography>
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
            <Grid item xs={12} sx={{ textAlign: 'end' }}>
              <Button
                variant="outlined"
                color="inherit"
                sx={styles.editButton}
                onClick={() => {
                  navigate(
                    `/publisher/mypage/article/edit/${data.articleId ?? ''}`,
                    {
                      state: { article }
                    }
                  );
                }}
              >
                {t('mypage.articleList.edit')}
              </Button>
            </Grid>
          </Grid>
        </Grid>
      </Card>
    </>
  );
};

export default ArticleCard;
